import { useEffect, useState } from "react";
import { toast } from "sonner";
import { getAllStudentByClass } from "../../../api/etudiants";
import { getAllClass } from "../../../api/pedagogicElement";
import { useAcademicYear } from "../../../core/common/context/AcademicYearContext";
import {
  Action,
  buildPermissionContext,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";
import StudentSanctionDetails from "./studentSanctionDetails";
import { Select } from "antd";
import Avvvatars from "avvvatars-react";
import DashboardMenu from "../../user/admin/dashboardMenu";
import InfosAlertYearDisabled from "../../../core/common/alerts/infosAlertYearDisabled";

const SanctionPage = () => {
  const { selectedYear } = useAcademicYear();
  const [allClass, setAllClass] = useState<any[]>([]);
  const [selectedClass, setSelectedClass] = useState<string>();
  const [allStudents, setAllStudents] = useState<any[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const fetchAllClassByYear = async () => {
    try {
      if (!selectedYear) return;

      const response = await getAllClass(
        selectedYear?.id,
        buildPermissionContext(Resource.STUDENT_SANCTION, Action.READ),
      );
      const classes =
        response.data
          .slice() // pour éviter de modifier directement la réponse
          .sort((a: any, b: any) => {
            // Tri par Level.label
            const levelCompare = a.Level.label.localeCompare(
              b.Level.label,
              "fr",
              { numeric: true },
            );
            if (levelCompare !== 0) return levelCompare;

            // Si même Level, tri par class label
            return a.label.localeCompare(b.label, "fr", { numeric: true });
          }) || [];
      setAllClass(classes);
      // console.log("Classes: ", classes);
      if (classes.length > 0) {
        // Select first class by default when classes are loaded or year changes
        setSelectedClass(classes[0].id);
      } else {
        setSelectedClass(undefined);
      }
    } catch (error: any) {
      // console.log("Erreur lors de la récupération des classes:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  const fetchStudentsByClass = async () => {
    if (!selectedClass) return;
    setLoading(true);
    try {
      const response = await getAllStudentByClass(
        selectedClass,
        buildPermissionContext(Resource.STUDENT_SANCTION, Action.READ),
      );
      setAllStudents(response.data || []);
      // console.log("All student: ", response.data);
      // Reset selected student when class changes
      setSelectedStudent(null);
    } catch (error: any) {
      // console.error("Erreur chargement étudiants:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllClassByYear();
  }, [selectedYear]);

  useEffect(() => {
    if (selectedClass) {
      fetchStudentsByClass();
    }
  }, [selectedClass]);

  return (
    <div className="page-wrapper">
      <div
        className="content container-fluid d-flex flex-column"
        style={{ height: "calc(100vh - 60px)" }}
      >
        <div
          className="sticky-top bg-white d-md-flex d-block align-items-center justify-content-between mb-3"
          style={{ zIndex: 1020, top: 0 }}
        >
          {/* MENU DASHBOARD */}
          <DashboardMenu />

          {/* Class Selector */}
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <div className="">
              <div className="card">
                <div className="card-body p-3">
                  <label className="form-label fw-bold">
                    Sélectionner une classe
                  </label>
                  <Select
                    className="w-100"
                    placeholder="Choisir une classe"
                    value={selectedClass}
                    onChange={(value) => setSelectedClass(value)}
                    options={allClass.map((cls) => ({
                      value: cls.id,
                      label: `${cls?.Level?.abbreviation} - ${cls.label}`,
                    }))}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        {!selectedYear?.statut && <InfosAlertYearDisabled />}

        <div
          className="row flex-grow-1"
          style={{ minHeight: 0, height: "100%", overflow: "hidden" }}
        >
          {/* Student List */}
          <div
            className={
              selectedStudent
                ? "col-12 col-md-5 col-lg-4 d-flex flex-column"
                : "col-12 d-flex flex-column"
            }
            style={{ minHeight: 0, maxHeight: "100%" }}
          >
            {loading ? (
              <div className="text-center p-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Chargement...</span>
                </div>
              </div>
            ) : allStudents.length === 0 ? (
              <div className="card">
                <div className="card-body text-center p-5">
                  <i className="ti ti-users fs-1 mb-3 text-muted" />
                  <h5>Aucun étudiant trouvé</h5>
                  <p className="text-muted">
                    Sélectionnez une classe pour voir les étudiants.
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex-grow-1 p-2" style={{ overflowY: "auto" }}>
                <div
                  className={`row g-3 ${
                    selectedStudent
                      ? "row-cols-1 row-cols-sm-2 row-cols-md-2 row-cols-lg-2"
                      : "row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 row-cols-xl-5"
                  }`}
                >
                  {allStudents.map((student) => (
                    <div key={student.id} className="col">
                      <div
                        className={`card h-100 cursor-pointer transition-all ${
                          selectedStudent?.StudentProfil?.id ===
                          student?.StudentProfil?.id
                            ? "border-primary border-2 shadow"
                            : "hover-shadow"
                        }`}
                        onClick={() => setSelectedStudent(student)}
                        style={{ cursor: "pointer", transition: "all 0.2s" }}
                      >
                        <div className="card-body text-center">
                          <div className="mb-3">
                            <span className="avatar avatar-xl rounded-circle">
                              <Avvvatars
                                value={`${
                                  student?.StudentProfil?.firstname?.charAt(
                                    0,
                                  ) || ""
                                }${
                                  student?.StudentProfil?.lastname?.charAt(0) ||
                                  ""
                                }`}
                                style="character" // ou "character"
                              />
                            </span>
                          </div>
                          {/* <Link
                            to={`/student-detail/${student?.StudentProfil?.id}`}
                          > */}
                          <h6 className="fw-bold mb-1">
                            {student?.StudentProfil?.firstname}{" "}
                            {student?.StudentProfil?.lastname}
                          </h6>
                          <p className="text-muted small mb-2">
                            {student?.StudentProfil?.matricule}
                          </p>
                          {/* </Link> */}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sanction Details Panel */}
          {selectedStudent && (
            <div
              className="col-12 col-md-7 col-lg-8 d-flex flex-column"
              style={{ minHeight: 0, maxHeight: "100%" }}
            >
              <div
                className="flex-grow-1"
                style={{ overflowY: "auto", minHeight: 0 }}
              >
                <div className="card border-0 shadow-none bg-transparent mb-0">
                  <div className="card-header bg-transparent border-0 px-0 pb-2 d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Détails des sanctions</h5>
                    <button
                      className="btn btn-sm btn-icon btn-light rounded-circle"
                      onClick={() => setSelectedStudent(null)}
                    >
                      <i className="ti ti-x" />
                    </button>
                  </div>
                </div>
                <StudentSanctionDetails
                  studentId={selectedStudent?.StudentProfil?.id}
                  studentData={selectedStudent?.StudentProfil}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SanctionPage;
